import { CdkDrag, CdkDragDrop, CdkDragStart, moveItemInArray, transferArrayItem } from "@angular/cdk/drag-drop";
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChange } from "@angular/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { TableColumn, TableColumnType, TableData, TableKey, TransferItem } from "./transfer-table/transfer-table.type";
import { TransferData } from "./transfer.type";

@Component({
  selector: "app-transfer",
  templateUrl: "./transfer.component.html",
  styleUrls: ["./transfer.component.scss"],
})
export class TransferComponent<T extends TransferItem> implements OnChanges {
  @Input() data: TransferData<T>;
  @Output() dataSourceChange: EventEmitter<T[]> = new EventEmitter();

  source: TableData<T>;
  target: TableData<T>;
  displayedColumns: string[];

  bodyElement: HTMLElement = document.body;

  // #region Life cycle methods

  ngOnChanges(changes: { [property: string]: SimpleChange }): void {
    if (changes.data) {
      this.reset();
    }
  }

  // #endregion

  // #region Handlers

  onDragStarted(_event: CdkDragStart): void {
    this.bodyElement.classList.add("inheritCursors");
    this.bodyElement.style.cursor = "grabbing";
  }

  onDropItem(table: TableKey, event: CdkDragDrop<TransferItem[]>): void {
    this.bodyElement.classList.remove("inheritCursors");
    this.bodyElement.style.cursor = "unset";

    const { item, container, previousContainer, previousIndex, currentIndex } = event;
    const isSameTable = previousContainer === container;
    this.transferItem(table, item, isSameTable, previousIndex, currentIndex);
  }

  onTransferItems(table: TableKey): void {
    this.transferItems(table);
  }

  onSortColumn(table: TableKey, event: { active: string; direction: "asc" | "desc" | "" }): void {
    const { active, direction } = event;
    this.sortDataSource(table, active, direction);
  }

  onSelectItem(table: TableKey, event: T): void {
    const { id } = event;
    this.selectItem(table, id);
  }

  onSelectAllItems(table: TableKey, column: TableColumn<T>, event: MatCheckboxChange): void {
    const { checked } = event;
    this.selectAllItems(table, column, checked);
  }

  // #endregion

  // #region Methods

  /** Set default column type for those columns without type */
  private setDefaultColumnTypes(type: TableColumnType): void {
    for (const column of this.data.columns) {
      if (!column.type) {
        column.type = type;
      }
    }
  }

  /** Set columns to be displayed on tables (source and target) */
  private setDisplayedColumns(): void {
    this.displayedColumns = [...this.data.columns.map((column) => column.id)];
  }

  /** Reset transfer component based on transfer data values */
  private reset(): void {
    this.setDefaultColumnTypes("input");
    this.setDisplayedColumns();
    this.setSourceAndTarget();
    this.unselectCheckboxColumns("source");
    this.unselectCheckboxColumns("target");
  }

  /** Set the tables source and target with the passed data to the component */
  private setSourceAndTarget(): void {
    this.source = { ...this.data, columns: this.data.columns.map((column) => ({ ...column })) };
    this.target = { ...this.data, columns: this.data.columns.map((column) => ({ ...column })), dataSource: [] };
  }

  /** Transfer item by dropping it */
  private transferItem(
    table: TableKey,
    item: CdkDrag<TransferItem>,
    isSameTable: boolean,
    previousIndex: number,
    currentIndex: number,
  ): void {
    if (isSameTable) {
      if (table === "source") {
        moveItemInArray(this.source.dataSource, previousIndex, currentIndex);
        this.source.dataSource = this.source.dataSource.slice();
      } else {
        moveItemInArray(this.target.dataSource, previousIndex, currentIndex);
        this.target.dataSource = this.target.dataSource.slice();
      }
    } else {
      if (table === "source") {
        transferArrayItem(this.target.dataSource, this.source.dataSource, previousIndex, currentIndex);
      } else {
        transferArrayItem(this.source.dataSource, this.target.dataSource, previousIndex, currentIndex);
      }

      this.source.dataSource = this.source.dataSource
        .map((data) => (item.data.id === data.id ? { ...data, selected: false } : data))
        .slice();
      this.target.dataSource = this.target.dataSource
        .map((data) => (item.data.id === data.id ? { ...data, selected: false } : data))
        .slice();

      const otherTable = table === "source" ? "target" : "source";

      if (!this[otherTable].dataSource.length) {
        this.unselectCheckboxColumns(otherTable);
      }
    }
    this.dataSourceChange.emit(this.target.dataSource);
  }

  /** Transfer selected items */
  private transferItems(table: TableKey): void {
    const elements = [...this.getDataSourceByTable(table)]
      .filter((row) => row.selected)
      .map((row) => ({ ...row, selected: false }));

    if (table === "source") {
      this.source.dataSource = [...this.getDataSourceByTable("source")].filter((row) => !row.selected);
      this.target.dataSource = [...this.getDataSourceByTable("target"), ...elements];
    } else {
      this.target.dataSource = [...this.getDataSourceByTable("target")].filter((row) => !row.selected);
      this.source.dataSource = [...this.getDataSourceByTable("source"), ...elements];
    }

    this.unselectCheckboxColumns(table);

    this.dataSourceChange.emit(this.target.dataSource);
  }

  /** Select an item */
  private selectItem(table: TableKey, id: string): void {
    for (const element of this.getDataSourceByTable(table)) {
      if (element.id === id) {
        element.selected = !element.selected;
        break;
      }
    }
  }

  /** Select all items */
  private selectAllItems(table: TableKey, column: TableColumn<T>, checked: boolean): void {
    const col = this[table].columns.find((_column) => _column.id === column.id);
    col.checked = checked;

    for (const element of this.getDataSourceByTable(table)) {
      element.selected = col.checked;
    }
  }

  /** Unselect all items */
  private unselectCheckboxColumns(table: TableKey): void {
    for (const column of this[table].columns) {
      if (column.type === "checkbox") {
        column.checked = false;
      }
    }
  }

  /** Get the table data source by key */
  private getDataSourceByTable(table: TableKey): T[] {
    return this[table].dataSource;
  }

  /** Sort data source */
  private sortDataSource(table: TableKey, key: string, direction: "asc" | "desc" | ""): void {
    const isAsc = direction === "asc";
    const isSorted = key && direction !== "";

    if (table === "source") {
      this.source.dataSource = [...this.getDataSourceByTable("source")].sort((a, b) =>
        isSorted ? this.compare(a[key], b[key], isAsc) : 0,
      );
    } else {
      this.target.dataSource = [...this.getDataSourceByTable("target")].sort((a, b) =>
        isSorted ? this.compare(a[key], b[key], isAsc) : 0,
      );
    }
  }

  private compare(a: number | string, b: number | string, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  // #endregion
}
